<script lang="ts" setup>
import { useKeenSlider } from 'keen-slider/vue'
import 'keen-slider/keen-slider.min.css'

const props = defineProps<{
  specs: {
    id: number
    cover_tech_spec_id: {
      id: number
      name: string
      image: string
      translations: DirectusTranslation<{
        specs: {
          spec_name: string
          spec_value: string
        }[]
      }>
    }
  }[]
  data: {
    id: number
    translations: DirectusTranslation<{
      title: string
      section_title: string
    }>
  }
}>()

const { dt } = useDirectusTranslation()
const { hyphen } = useHyphen()

const specs = computed(() => {
  return props.specs.map((item) => {
    return {
      ...item.cover_tech_spec_id,
      ...dt(item.cover_tech_spec_id.translations),
      translations: undefined,
    }
  })
})

const activeSlide = ref(0)

const [container, slider] = useKeenSlider({
  loop: false,
  renderMode: 'performance',
  drag: true,
  slides: {
    perView: 1,
  },
  slideChanged: (s) => {
    // console.log('slide changed', s.track.details.rel)
    activeSlide.value = s.track.details.rel
  },
})

function nextSlide() {
  slider?.value?.next()
}
function prevSlide() {
  slider?.value?.prev()
}

slider.value?.moveToIdx(0)

const activeSpecIndex = computed({
  get: () => activeSlide.value,
  set: (value) => {
    if (value !== activeSlide.value)
      slider?.value?.moveToIdx(value)
  },
})

const activeSpecIndexString = computed({
  get: () => activeSlide.value.toString(),
  set: (value) => {
    activeSpecIndex.value = Number.parseInt(value)
  },

})

const activeSpec = computed(() => {
  return specs.value[activeSpecIndex.value]
})
</script>

<template>
  <BlockBase is-light class="bg-neutral-white py-15rem text-neutral-black">
    <Container class="grid grid-cols-1 w-full gap-10rem lg:grid-cols-2">
      <!-- SLIDER -->
      <div
        ref="container" class="keen-slider my-auto h-65rem w-full"
      >
        <div v-for="spec, index in specs" :key="index" class="keen-slider__slide size-full">
          <NuxtImg
            loading="lazy"
            provider="directus"
            :src="spec.image"
            :alt="spec.name"
            class="h-full w-full object-cover"
            height="650"
            width="650"
          />
        </div>

        <div v-if="specs.length > 1" class="absolute bottom-0 right-0 h-10rem w-15rem flex items-center justify-center bg-neutral-white">
          <button class="h-10rem w-7.5rem flex items-center justify-center transition hover:bg-starline-secondary/10" @click="prevSlide">
            <IconSmallArrow class="h-2rem w-2rem" />
          </button>
          <button class="h-10rem w-7.5rem flex items-center justify-center transition hover:bg-starline-secondary/10" @click="nextSlide">
            <IconSmallArrow class="h-2rem w-2rem rotate-180" />
          </button>
        </div>
      </div>

      <!-- Content -->
      <div
        class="my-auto w-full"
      >
        <h1 class="mb-4rem text-balance textH1Black">
          {{ hyphen(dt(props.data.translations)?.title) }}
        </h1>
        <TextWithAfterLine v-if="dt(props.data.translations)?.section_title" class="mb-2rem" :text="dt(props.data.translations)?.section_title ?? ''" />
        <div class="mt-8rem">
          <RadixRadioGroupRoot
            v-model="activeSpecIndexString"
          >
            <div class="w-full flex flex-wrap gap-2rem">
              <RadixRadioGroupItem
                v-for="item, index in specs" :key="index" :value="index.toString()"
                class="inline-block cursor-pointer select-none bg-starline-secondary px-1.6rem py-.8rem text-1.3rem text-neutral-white tracking-[.4rem] uppercase shadow-none ring ring-neutral-darkGray transition duration-300ms data-[active=true]:shadow-starline data-[active=true]:ring-3 data-[active=true]:ring-starline-primary hover:ring-starline-primary"
              >
                {{ item.name }}
                <!-- <RadixRadioGroupIndicator>indicator</RadixRadioGroupIndicator> -->
              </RadixRadioGroupItem>
            </div>
          </RadixRadioGroupRoot>
        </div>
        <div class="grid grid-cols-2 mt-8rem w-full gap-2rem">
          <div v-for="spec, index in activeSpec?.specs" :key="index">
            <h2 class="text-1.6rem">
              {{ spec.spec_name }}
            </h2>
            <p class="mt-1rem text-1.1rem text-neutral-gray tracking-[.3rem] uppercase">
              {{ spec.spec_value }}
            </p>
          </div>
        </div>
      </div>
    </Container>
  </BlockBase>
</template>

<style>

</style>
